import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate } from "../lib/format";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileSpreadsheet, AlertCircle, Receipt } from "lucide-react";
import { PERIOD_OPTIONS, periodRange } from "../lib/period";

export default function EFaktur() {
  const [preset, setPreset] = useState("this_month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [label, setLabel] = useState("Bulan Ini");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const r = periodRange(key);
    setFromDate(r.from); setToDate(r.to); setLabel(r.label);
  };

  useEffect(() => { applyPreset("this_month"); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = [];
      if (fromDate) q.push(`from_date=${fromDate}`);
      if (toDate) q.push(`to_date=${toDate}`);
      const r = await api.get(`/tax/e-faktur/preview${q.length ? "?" + q.join("&") : ""}`);
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat preview"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (fromDate || toDate) load(); /* eslint-disable-next-line */ }, [fromDate, toDate]);

  const downloadCsv = async () => {
    if (!preview || preview.count === 0) return toast.error("Tidak ada faktur untuk diekspor");
    try {
      const token = localStorage.getItem("erp_token");
      const q = [];
      if (fromDate) q.push(`from_date=${fromDate}`);
      if (toDate) q.push(`to_date=${toDate}`);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/tax/e-faktur/csv${q.length ? "?" + q.join("&") : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return toast.error(err.detail || "Gagal ekspor");
      }
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `e-faktur_${fromDate || "all"}_${toDate || "all"}.csv`;
      link.click();
      toast.success("CSV e-Faktur DJP diunduh — siap import ke aplikasi e-Faktur Client");
    } catch (e) { toast.error("Gagal ekspor"); }
  };

  const missing = (preview?.rows || []).filter((r) => !r.customer_npwp_valid).length;

  return (
    <div className="space-y-6" data-testid="efaktur-page">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Pelaporan Pajak</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">e-Faktur DJP</h1>
        <p className="text-sm text-slate-500 mt-1">Ekspor Faktur Pajak Keluaran (PPN 12%) ke format CSV yang siap di-import ke aplikasi <strong>e-Faktur Client Desktop DJP</strong> untuk pelaporan SPT Masa PPN.</p>
      </div>

      <Card className="card-elevated bg-navy-900 text-white border-0">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Label className="text-white/70">Periode Pelaporan</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="efaktur-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIOD_OPTIONS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Label className="text-white/70">Dari</Label>
            <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPreset("custom"); setLabel("Custom"); }} className="bg-white/10 border-white/20 text-white" />
          </div>
          <div className="w-44">
            <Label className="text-white/70">Sampai</Label>
            <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPreset("custom"); setLabel("Custom"); }} className="bg-white/10 border-white/20 text-white" />
          </div>
          <div className="flex-1 min-w-[180px] text-right">
            <div className="text-[10px] uppercase tracking-widest text-yellow-400">Periode Aktif</div>
            <div className="font-display font-bold text-xl">{label}</div>
          </div>
          <Button className="bg-yellow-500 hover:bg-yellow-600 text-navy-900 font-bold" onClick={downloadCsv} disabled={!preview || preview.count === 0} data-testid="efaktur-download-btn">
            <FileSpreadsheet size={16} className="mr-2" />Ekspor CSV DJP
          </Button>
        </CardContent>
      </Card>

      {preview && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="card-elevated"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Receipt size={12} />Jumlah Faktur</div>
              <div className="font-display text-2xl font-extrabold mt-1 text-navy-900">{preview.count}</div>
            </CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Total DPP</div>
              <div className="font-display text-xl font-extrabold mt-1 text-slate-800">{formatIDR(preview.total_dpp)}</div>
            </CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Total PPN 12%</div>
              <div className="font-display text-xl font-extrabold mt-1 text-yellow-700">{formatIDR(preview.total_ppn)}</div>
            </CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Total DPP + PPN</div>
              <div className="font-display text-xl font-extrabold mt-1 text-emerald-700">{formatIDR(preview.total_dpp + preview.total_ppn)}</div>
            </CardContent></Card>
          </div>

          {missing > 0 && (
            <Card className="border-yellow-300 bg-yellow-50">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="text-yellow-700 flex-shrink-0" size={20} />
                <div className="text-sm text-yellow-900">
                  <strong>{missing} faktur belum memiliki NPWP customer.</strong> DJP mensyaratkan NPWP lawan transaksi.
                  Sistem akan menulis NPWP <code className="bg-yellow-100 px-1 rounded">000000000000000</code> yang berarti "non-PKP".
                  Lengkapi NPWP di halaman <strong>Customer</strong> agar faktur diakui sebagai faktur normal.
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card-elevated"><CardContent className="p-4">
            <div className="font-display font-bold text-slate-800 mb-3">Preview Faktur ({preview.count})</div>
            <div className="overflow-x-auto">
              <Table data-testid="efaktur-table">
                <TableHeader><TableRow>
                  <TableHead>No. Invoice</TableHead>
                  <TableHead>No. Faktur Pajak</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>NPWP</TableHead>
                  <TableHead className="text-right">DPP</TableHead>
                  <TableHead className="text-right">PPN</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {preview.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                      <TableCell className="font-mono text-xs font-semibold text-navy-900">{r.faktur_number}</TableCell>
                      <TableCell className="text-sm">{formatDate(r.issue_date)}</TableCell>
                      <TableCell>{r.customer_name}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.customer_npwp_valid
                          ? <span className="text-emerald-700">{r.customer_npwp}</span>
                          : <span className="text-red-600 inline-flex items-center gap-1"><AlertCircle size={12} />NPWP kosong</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatIDR(r.dpp)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-yellow-700">{formatIDR(r.ppn)}</TableCell>
                      <TableCell><Badge className={r.status === "paid" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-yellow-100 text-yellow-800 border-yellow-200"}>{r.status.toUpperCase()}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {preview.count === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Tidak ada Faktur Pajak Keluaran di periode ini{loading && " (memuat...)"}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent></Card>

          <Card className="card-elevated bg-slate-50">
            <CardContent className="p-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Cara Pakai</div>
              <ol className="text-sm text-slate-700 space-y-1 list-decimal ml-5">
                <li>Pastikan setiap customer B2B memiliki NPWP di halaman Customer.</li>
                <li>Pilih periode masa pajak (biasanya per bulan) lalu klik <strong>Ekspor CSV DJP</strong>.</li>
                <li>Buka <strong>Aplikasi e-Faktur Client Desktop</strong> milik DJP di komputer Anda.</li>
                <li>Menu → <em>Faktur → Pajak Keluaran → Import</em> → pilih file CSV yang barusan diunduh.</li>
                <li>Review, upload ke server DJP, lalu approve untuk mendapatkan nomor seri resmi.</li>
              </ol>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
